import { useMemo } from 'react'

import {
  F0AvatarPerson,
  F0Box,
  F0Button,
  F0Heading,
  F0TagStatus,
  F0Text,
} from '@factorialco/f0-react'
import { Cross } from '@factorialco/f0-react/icons/app'
import { OneEmptyState, Tooltip } from '@factorialco/f0-react/dist/experimental'

import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import styles from './VersionHistory.module.css'

interface Author {
  id: string
  firstName: string
  lastName: string
  avatar?: { id: string; url: string } | null
}

export interface VersionNode {
  id: string
  versionNumber: number
  status: string
  publishedAt?: string | null
  createdAt: string
  updatedAt: string
  workflowId: string
  formId: string
  basedOnVersionId?: string | null
  createdBy: Author
  publishedBy?: Author | null
}

interface VersionHistoryPanelProps {
  versions: VersionNode[]
  activeVersion: VersionNode | null
  draftVersions: VersionNode[]
  selectedVersionId: string | 'current'
  onSelectVersion: (id: string | 'current') => void
  onClose: () => void
}

interface MonthGroup {
  key: string
  label: string
  versions: VersionNode[]
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString(i18n.locale || undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

function groupVersionsByMonth(versions: VersionNode[]): MonthGroup[] {
  const groups = new Map<string, VersionNode[]>()

  const sorted = [...versions].sort((a, b) => {
    const dateA = new Date(a.publishedAt ?? a.updatedAt)
    const dateB = new Date(b.publishedAt ?? b.updatedAt)
    return dateB.getTime() - dateA.getTime()
  })

  for (const version of sorted) {
    const date = new Date(version.publishedAt ?? version.updatedAt)
    const key = `${date.getFullYear()}-${date.getMonth()}`

    const existing = groups.get(key)
    if (existing) {
      existing.push(version)
    } else {
      groups.set(key, [version])
    }
  }

  return Array.from(groups.entries()).map(([groupKey, groupVersions]) => {
    const first = groupVersions[0]
    if (!first) return { key: groupKey, label: '', versions: groupVersions }
    const date = new Date(first.publishedAt ?? first.updatedAt)
    const monthName = date.toLocaleDateString(i18n.locale || undefined, {
      month: 'long',
      year: 'numeric',
    })
    return {
      key: groupKey,
      label: `${monthName} (${groupVersions.length})`,
      versions: groupVersions,
    }
  })
}

function VersionStatusBadge({ status }: { status: string }) {
  if (status === 'active') {
    return (
      <F0TagStatus
        text={i18n.t('procurement.types.version_history.status.live')}
        variant="positive"
      />
    )
  }
  if (status === 'draft') {
    return (
      <F0TagStatus
        text={i18n.t('procurement.types.version_history.status.draft')}
        variant="neutral"
      />
    )
  }
  return null
}

function VersionItem({
  version,
  isSelected,
  onClick,
}: {
  version: VersionNode
  isSelected: boolean
  onClick: () => void
}) {
  const author = version.publishedBy ?? version.createdBy
  const dateStr = version.publishedAt ?? version.updatedAt
  const isLive = version.status === 'active'

  const content = (
    <button
      type="button"
      onClick={onClick}
      className={styles.versionItem}
      data-selected={isSelected}
    >
      <F0Box display="flex" alignItems="center" gap="sm">
        <F0Text
          content={i18n.t('procurement.types.version_history.version_label', {
            versionNumber: version.versionNumber,
          })}
          variant="label"
        />
        <VersionStatusBadge status={version.status} />
      </F0Box>
      <F0Box display="flex" alignItems="center" gap="sm">
        <F0Text content={formatShortDate(dateStr)} variant="description" />
        <F0Box display="flex" alignItems="center" gap="xs">
          <F0AvatarPerson
            firstName={author.firstName}
            lastName={author.lastName}
            src={buildUrl(author.avatar?.url)}
            size="xs"
          />
          <F0Text content={`${author.firstName} ${author.lastName}`} variant="label" ellipsis />
        </F0Box>
      </F0Box>
    </button>
  )

  if (isLive) {
    return (
      <Tooltip description={i18n.t('procurement.types.version_history.live_tooltip')}>
        {content}
      </Tooltip>
    )
  }

  return content
}

export function VersionHistoryPanel({
  versions,
  activeVersion,
  draftVersions,
  selectedVersionId,
  onSelectVersion,
  onClose,
}: VersionHistoryPanelProps) {
  const archivedVersions = useMemo(
    () => versions.filter((v) => v.status === 'archived'),
    [versions]
  )

  const monthGroups = useMemo(() => groupVersionsByMonth(archivedVersions), [archivedVersions])

  return (
    <F0Box display="flex" flexDirection="column" grow overflow="hidden">
      <F0Box
        display="flex"
        alignItems="center"
        justifyContent="between"
        paddingX="lg"
        paddingY="md"
        shrink={false}
      >
        <F0Text
          content={i18n.t('procurement.types.version_history.title')}
          variant="label"
          as="span"
        />
        <F0Button
          variant="outline"
          icon={Cross}
          onClick={onClose}
          label={i18n.t('common.close')}
          hideLabel
        />
      </F0Box>

      {versions.length === 0 ? (
        <F0Box display="flex" alignItems="center" justifyContent="center" grow padding="lg">
          <OneEmptyState
            title={i18n.t('procurement.types.version_history.no_versions.title')}
            description={i18n.t('procurement.types.version_history.no_versions.description')}
          />
        </F0Box>
      ) : (
        <div className={styles.panelScroll}>
          <F0Box display="flex" flexDirection="column">
            {draftVersions.map((draft) => (
              <VersionItem
                key={draft.id}
                version={draft}
                isSelected={selectedVersionId === String(draft.id)}
                onClick={() => onSelectVersion(String(draft.id))}
              />
            ))}

            {activeVersion && (
              <VersionItem
                version={activeVersion}
                isSelected={selectedVersionId === 'current'}
                onClick={() => onSelectVersion('current')}
              />
            )}

            {archivedVersions.length > 0 && (
              <>
                <F0Box borderTop="default" borderColor="secondary" marginX="sm" marginY="md" />

                <F0Box paddingX="sm" paddingBottom="sm">
                  <F0Heading
                    content={i18n.t('procurement.types.version_history.other_versions')}
                    variant="heading"
                  />
                </F0Box>

                <F0Box display="flex" flexDirection="column" gap="lg">
                  {monthGroups.map((group) => (
                    <F0Box key={group.key} display="flex" flexDirection="column">
                      <F0Box paddingX="sm">
                        <F0Text content={group.label} variant="description" />
                      </F0Box>
                      {group.versions.map((version) => (
                        <VersionItem
                          key={version.id}
                          version={version}
                          isSelected={selectedVersionId === String(version.id)}
                          onClick={() => onSelectVersion(String(version.id))}
                        />
                      ))}
                    </F0Box>
                  ))}
                </F0Box>
              </>
            )}
          </F0Box>
        </div>
      )}
    </F0Box>
  )
}
